import React, { useEffect, useRef, useState } from "react";
import { Animated, ImageProps, StyleSheet, View } from "react-native";

type FadeImageProps = Omit<ImageProps, "source"> & {
  source: { uri: string };
  duration?: number;
};

const FadeImage: React.FC<FadeImageProps> = ({ source, duration = 1000, style, ...rest }) => {
  const [prevImage, setPrevImage] = useState<{ uri: string } | null>(null);
  const [currentImage, setCurrentImage] = useState(source);

  const fadeAnim = useRef(new Animated.Value(1)).current; // current image opacity
  const nextAnim = useRef(new Animated.Value(0)).current; // next image opacity

  useEffect(() => {
    if(!prevImage){
        setPrevImage(currentImage);
    }
    if (currentImage && source.uri === currentImage.uri) return;

    // Set previous to current, and update new
    setPrevImage(currentImage);
    setCurrentImage(source);

    // Reset animations
    nextAnim.setValue(0);

    // Animate: fade in new
    Animated.timing(nextAnim, {
      toValue: 1,
      duration,
      useNativeDriver: true,
    }).start(() => {
      // After fade, clear prevImage so only one stays mounted
      setPrevImage(null);
    });
  }, [source]);

  return (
    <View style={[styles.container, style]}>
      {/* Previous image (fading out) */}
      {prevImage && (
        <Animated.Image
          {...rest}
          source={prevImage}
          style={[StyleSheet.absoluteFillObject, { opacity: fadeAnim }]}
        />
      )}

      {/* Current image (fading in) */}
      <Animated.Image
        {...rest}
        source={currentImage}
        style={[StyleSheet.absoluteFillObject, { opacity: nextAnim }]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    overflow: "hidden",
  },
});

export default FadeImage;
